<template>
  <CModal :onEsc="onCancel" show>
    <CCard aria-modal="true" :bordered="false" class="w-[640px]" role="dialog" size="medium">
      <div class="text-xl font-500 mb-4" :style="{ color: themeVars.text }">
        {{ title }}
      </div>

      <div class="mb-2">
        <div class="text-body-2 font-400 mb-1">
          {{ nameLabel }}
        </div>
        <NInput
          v-model:value="localValue"
          :placeholder="placeholder"
          @keydown="($event) => $event.stopPropagation()"
          @keydown.enter="onSubmit"
        />
      </div>

      <div v-if="description" class="text-sm mb-6" :style="{ color: themeVars.text3 }">
        {{ description }}
      </div>

      <div class="flex justify-between items-center">
        <CButton @click="onCancel">
          {{ cancelText }}
        </CButton>
        <CButton :disabled="!isValid" type="primary" @click="onSubmit">
          {{ confirmText }}
        </CButton>
      </div>
    </CCard>
  </CModal>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { NInput } from 'naive-ui'

import CCard from '@lib/components/CCard/CCard.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CModal from '@lib/components/CModal/CModal.vue'
import useThemeVars from '@lib/composables/useThemeVars'

interface Props {
  modelValue: string
  title?: string
  nameLabel?: string
  placeholder?: string
  description?: string
  cancelText?: string
  confirmText?: string
  required?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  nameLabel: '',
  placeholder: '',
  description: '',
  cancelText: '',
  confirmText: '',
  required: true,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  confirm: [value: string]
  cancel: []
}>()

const { t } = useI18n()
const themeVars = useThemeVars()

const localValue = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
})

const isValid = computed(() => {
  if (!props.required) return true
  return localValue.value.trim().length > 0
})

const title = computed(() => props.title || t('canvasUI.canvasBuilder.reorder.renameTitle'))
const nameLabel = computed(() => props.nameLabel || t('canvasUI.canvasBuilder.reorder.nameLabel'))
const placeholder = computed(() => props.placeholder || t('canvasUI.canvasBuilder.reorder.namePlaceholder'))
const description = computed(() => props.description || t('canvasUI.canvasBuilder.reorder.renameDescription'))
const cancelText = computed(() => props.cancelText || t('canvasUI.common.cancel'))
const confirmText = computed(() => props.confirmText || t('canvasUI.common.done'))

function onSubmit() {
  if (!isValid.value) return
  emit('confirm', localValue.value)
}

function onCancel() {
  localValue.value = props.modelValue
  emit('cancel')
}
</script>
